import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getPreventivo, inviaPreventivo, segnaPreventivoInviato, accettaPreventivoManuale, scaricaPdfPreventivo } from '../services/api';
import { Container, Card, Button, Row, Col, Spinner, Alert, Table, Form } from 'react-bootstrap';
import { CONDIZIONI_PAGAMENTO } from '../common/preventivoUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEnvelope, faClipboardCheck, faLink, faPrint, faFilePdf, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal'; // Modale di conferma

function PreventivoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/preventivi');
  const [preventivo, setPreventivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [includiLink, setIncludiLink] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getPreventivo(id);
        setPreventivo(response.data);
      } catch (err) {
        setError('Errore nel caricamento dei dettagli del preventivo.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleSendPreventivo = async () => {
    setIsSending(true);
    try {
      const res = await inviaPreventivo(id, includiLink);
      toast.success('Preventivo inviato con il PDF in allegato!');
      setPreventivo(res.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Errore durante l\'invio del preventivo.';
      toast.error(`Errore: ${errorMsg}`);
      console.error(err);
    } finally {
      setIsSending(false);
      setShowSendModal(false);
    }
  };

  const handleAccettaManuale = async () => {
    setIsConverting(true);
    try {
      const response = await accettaPreventivoManuale(id);
      const ordine = response.data.ordine;
      toast.success(`Preventivo accettato: creato l'ordine #${ordine.id}.`);
      setPreventivo(response.data.preventivo);
      toast.info(`Clicca qui per aprire l'ordine #${ordine.id}.`, { onClick: () => navigate(`/ordini/${ordine.id}`) });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Errore durante l'accettazione del preventivo.";
      toast.error(`Errore: ${errorMsg}`);
      console.error(err);
    } finally {
      setIsConverting(false);
      setShowConvertModal(false);
    }
  };

  const handleSegnaInviato = async () => {
    try {
      const res = await segnaPreventivoInviato(id);
      setPreventivo(res.data);
      toast.success('Preventivo segnato come inviato.');
    } catch (err) {
      toast.error(`Errore: ${err.response?.data?.detail || 'operazione non riuscita'}`);
    }
  };

  const handleScaricaPdf = async () => {
    try {
      const res = await scaricaPdfPreventivo(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `preventivo_${String(preventivo.numero_completo || id).replace('/', '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Impossibile generare il PDF del preventivo.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Caricamento dettagli preventivo...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={tornaIndietro}>Torna alla lista preventivi</Button>
      </Container>
    );
  }

  if (!preventivo) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Preventivo non trovato.</Alert>
        <Button variant="primary" onClick={tornaIndietro}>Torna alla lista preventivi</Button>
      </Container>
    );
  }

  const isSent = preventivo.stato === 'Inviato' || preventivo.stato === 'Accettato' || preventivo.stato === 'Convertito in Ordine';
  const canSend = preventivo.stato === 'Richiesto' || preventivo.stato === 'In Bozza';
  // Accettazione registrata dal gestionale: vale finche' non c'e' ancora un ordine (anche se il cliente non ha usato il link)
  const isConvertible = ['Richiesto', 'In Bozza', 'Inviato', 'Accettato'].includes(preventivo.stato) && !preventivo.ordine_correlato_id;
  const isAlreadyConverted = !!preventivo.ordine_correlato_id;
  const publicLink = `${process.env.REACT_APP_ECOMMERCE_URL || 'http://localhost:3000'}/preventivo/${preventivo.token_accesso}`; // Link pubblico per il frontend e-commerce

  return (
    <>
      <Container className="mt-4">
        <Row className="d-print-none mb-3 align-items-center">
          <Col>
            <h2 className="mb-0">Preventivo N. {preventivo.numero_completo || preventivo.id}</h2>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Button variant="secondary" onClick={tornaIndietro} className="me-2">
              Torna alla Lista
            </Button>
            <Button variant="warning" onClick={() => navigate(`/preventivi/edit/${id}`)} className="me-2">
              <FontAwesomeIcon icon={faEdit} /> Modifica
            </Button>
            <Button variant="outline-danger" onClick={handleScaricaPdf} className="me-2">
              <FontAwesomeIcon icon={faFilePdf} /> Scarica PDF
            </Button>
            {canSend && (
              <Button 
                variant="primary" 
                onClick={() => setShowSendModal(true)} 
                disabled={isSending}
                className="me-2"
              >
                {isSending ? <Spinner as="span" animation="border" size="sm" /> : <><FontAwesomeIcon icon={faEnvelope} /> Invia email con PDF</>}
              </Button>
            )}
            {canSend && (
              <Button variant="outline-primary" onClick={handleSegnaInviato} className="me-2">
                <FontAwesomeIcon icon={faPaperPlane} /> Segna come inviato
              </Button>
            )}
            {isConvertible && (
              <Button 
                variant="success" 
                onClick={() => setShowConvertModal(true)} 
                disabled={isConverting}
                className="me-2"
              >
                {isConverting ? <Spinner as="span" animation="border" size="sm" /> : <><FontAwesomeIcon icon={faClipboardCheck} /> Segna accettato e crea ordine</>}
              </Button>
            )}
            {isAlreadyConverted && (
              <Button 
                variant="outline-success" 
                onClick={() => navigate(`/ordini/${preventivo.ordine_correlato_id}`)} 
                className="me-2"
              >
                <FontAwesomeIcon icon={faClipboardCheck} /> Vedi Ordine #{preventivo.ordine_correlato_id}
              </Button>
            )}
            <Button variant="info" onClick={() => window.open(publicLink, '_blank')} className="me-2">
                <FontAwesomeIcon icon={faPrint} /> Anteprima Pubblica
            </Button>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Header as="h5">Workflow Preventivo</Card.Header>
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <div className={`rounded-circle me-2 ${preventivo.stato === 'Richiesto' || preventivo.stato === 'In Bozza' ? 'bg-warning' : 'bg-success'}`} style={{ width: '20px', height: '20px' }}></div>
                <span className={preventivo.stato === 'Richiesto' || preventivo.stato === 'In Bozza' ? 'fw-bold' : ''}>1. Richiesta Cliente</span>
              </div>
              <div className="flex-grow-1 mx-3">
                <hr className={`my-0 ${preventivo.stato === 'In Bozza' || preventivo.stato === 'Inviato' || preventivo.stato === 'Accettato' || preventivo.stato === 'Convertito in Ordine' ? 'border-success border-2' : 'border-muted'}`} />
              </div>
              <div className="d-flex align-items-center">
                <div className={`rounded-circle me-2 ${preventivo.stato === 'Inviato' ? 'bg-warning' : preventivo.stato === 'Accettato' || preventivo.stato === 'Convertito in Ordine' ? 'bg-success' : 'bg-light'}`} style={{ width: '20px', height: '20px' }}></div>
                <span className={preventivo.stato === 'Inviato' ? 'fw-bold' : ''}>2. Invio a Cliente</span>
              </div>
              <div className="flex-grow-1 mx-3">
                <hr className={`my-0 ${preventivo.stato === 'Accettato' || preventivo.stato === 'Convertito in Ordine' ? 'border-success border-2' : 'border-muted'}`} />
              </div>
              <div className="d-flex align-items-center">
                <div className={`rounded-circle me-2 ${preventivo.stato === 'Accettato' ? 'bg-warning' : preventivo.stato === 'Convertito in Ordine' ? 'bg-success' : 'bg-light'}`} style={{ width: '20px', height: '20px' }}></div>
                <span className={preventivo.stato === 'Accettato' ? 'fw-bold' : ''}>3. Accettazione</span>
              </div>
              <div className="flex-grow-1 mx-3">
                <hr className={`my-0 ${preventivo.stato === 'Convertito in Ordine' ? 'border-success border-2' : 'border-muted'}`} />
              </div>
              <div className="d-flex align-items-center">
                <div className={`rounded-circle me-2 ${preventivo.stato === 'Convertito in Ordine' ? 'bg-success' : 'bg-light'}`} style={{ width: '20px', height: '20px' }}></div>
                <span className={preventivo.stato === 'Convertito in Ordine' ? 'fw-bold' : ''}>4. Ordine</span>
              </div>
            </div>
            <small className="text-muted">
              {preventivo.stato === 'Richiesto' && 'Il cliente ha inviato la richiesta. Puoi modificare il preventivo e inviarlo.'}
              {preventivo.stato === 'In Bozza' && 'Preventivo in lavorazione. Puoi modificarlo e inviarlo al cliente.'}
              {preventivo.stato === 'Inviato' && 'Preventivo inviato al cliente. In attesa di accettazione.'}
              {preventivo.stato === 'Accettato' && 'Cliente ha accettato il preventivo. Puoi convertirlo in ordine.'}
              {preventivo.stato === 'Convertito in Ordine' && 'Preventivo convertito in ordine. Processo completato.'}
              {preventivo.stato === 'Rifiutato' && 'Il preventivo è stato rifiutato dal cliente.'}
              {preventivo.stato === 'Scaduto' && 'Il preventivo è scaduto.'}
            </small>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header as="h5">Dettagli Preventivo</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p><strong>ID Preventivo:</strong> {preventivo.id}</p>
                <p><strong>Data Creazione:</strong> {new Date(preventivo.data_creazione).toLocaleDateString()}</p>
                {preventivo.data_scadenza && <p><strong>Data Scadenza:</strong> {new Date(preventivo.data_scadenza).toLocaleDateString()}</p>}
                <p><strong>Stato:</strong> 
                  <span 
                    className={`badge ms-2 ${
                      preventivo.stato === 'Richiesto' ? 'bg-warning text-dark' :
                      preventivo.stato === 'In Bozza' ? 'bg-secondary' :
                      preventivo.stato === 'Inviato' ? 'bg-primary' :
                      preventivo.stato === 'Accettato' ? 'bg-success' :
                      preventivo.stato === 'Convertito in Ordine' ? 'bg-info' :
                      preventivo.stato === 'Rifiutato' ? 'bg-danger' :
                      preventivo.stato === 'Scaduto' ? 'bg-dark' :
                      'bg-light text-dark'
                    }`}
                  >
                    {preventivo.stato}
                  </span>
                </p>
                {preventivo.ordine_correlato_id && (
                    <p><strong>Convertito in Ordine:</strong> <Link to={`/ordini/${preventivo.ordine_correlato_id}`}>#{preventivo.ordine_correlato_id}</Link></p>
                )}
              </Col>
              <Col md={6}>
                <p><strong>Cliente:</strong> {preventivo.cliente_nome || (preventivo.cliente ? `${preventivo.cliente.nome} ${preventivo.cliente.cognome || ''}` : 'N/A')}</p>
                {preventivo.cliente_email && <p><strong>Email Cliente:</strong> {preventivo.cliente_email}</p>}
                {preventivo.cliente_telefono && <p><strong>Telefono Cliente:</strong> {preventivo.cliente_telefono}</p>}
                {preventivo.note && <p><strong>Note interne:</strong> {preventivo.note}</p>}
                {preventivo.note_cliente && <p><strong>Note per il cliente:</strong> {preventivo.note_cliente}</p>}
                {(preventivo.condizioni_pagamento || []).length > 0 && (
                  <p>
                    <strong>Pagamento:</strong>{' '}
                    {CONDIZIONI_PAGAMENTO.filter(c => preventivo.condizioni_pagamento.includes(c.id)).map(c => c.label).join(' · ')}
                    {preventivo.acconto_calcolato != null && (
                      <><br /><small className="text-muted">Acconto € {preventivo.acconto_calcolato.toFixed(2)} · Saldo € {preventivo.saldo_calcolato.toFixed(2)}</small></>
                    )}
                    {preventivo.banca && preventivo.condizioni_pagamento.includes('BONIFICO') && (
                      <><br /><small className="text-muted">{preventivo.banca.denominazione} · {preventivo.banca.iban}</small></>
                    )}
                  </p>
                )}
              </Col>
            </Row>
            {preventivo.token_accesso && (
                <Row className="mt-3">
                    <Col>
                        <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <div>
                            <p className="mb-1"><strong>Link Pubblico Cliente:</strong></p> 
                            <small className="text-muted">Il cliente può visualizzare e accettare il preventivo tramite questo link</small>
                          </div>
                          <div>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => {
                                navigator.clipboard.writeText(publicLink);
                                toast.success('Link copiato negli appunti!');
                              }}
                              className="me-2"
                            >
                              Copia Link
                            </Button>
                            <Button 
                              variant="info" 
                              size="sm" 
                              onClick={() => window.open(publicLink, '_blank')}
                            >
                              <FontAwesomeIcon icon={faLink} /> Apri
                            </Button>
                          </div>
                        </div>
                    </Col>
                </Row>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header as="h5">Dettagli Servizi/Prodotti</Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Descrizione</th>
                  <th>Quantità</th>
                  <th>Prezzo Unit. (IVA incl.)</th>
                  <th>IVA</th>
                  <th>Totale Riga (IVA incl.)</th>
                </tr>
              </thead>
              <tbody>
                {preventivo.dettagli.map(d => {
                  const descrizione = d.descrizione || (d.servizio ? d.servizio.titolo_servizio : 'Prodotto Personalizzato');
                  const totaleRiga = d.quantita * d.prezzo_unitario; // il prezzo unitario e' gia' IVA inclusa
                  return (
                    <tr key={d.id}>
                      <td>{descrizione}</td>
                      <td>{d.quantita}</td>
                      <td>€ {d.prezzo_unitario.toFixed(2)}</td>
                      <td>{d.iva_percentuale}%</td>
                      <td>€ {totaleRiga.toFixed(2)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        
        <Card>
            <Card.Header as="h5">Riepilogo Preventivo</Card.Header>
            <Card.Body>
                <Row>
                    <Col md={{ span: 4, offset: 8 }}>
                        <p className="d-flex justify-content-between"><strong>Subtotale Imponibile:</strong> <span>€ {preventivo.subtotale.toFixed(2)}</span></p>
                        <p className="d-flex justify-content-between"><strong>IVA Totale:</strong> <span>€ {preventivo.iva_totale.toFixed(2)}</span></p>
                        <hr />
                        <h4 className="d-flex justify-content-between"><strong>Totale Preventivo (IVA inclusa):</strong> <span>€ {preventivo.totale.toFixed(2)}</span></h4>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
        
      </Container>

      <ConfirmationModal
        show={showSendModal}
        title="Conferma Invio Preventivo"
        body={
          <div>
            <p>Sei sicuro di voler inviare il preventivo {preventivo.numero_completo || `#${preventivo.id}`} al cliente?</p>
            <div className="alert alert-info mt-3">
              <strong>Email cliente:</strong> {preventivo.cliente_email || 'Non specificata'}<br/>
              <strong>Cosa succede:</strong> il cliente riceve un'email con il preventivo in PDF allegato.
            </div>
            <Form.Check
              type="switch"
              id="includi-link-invio"
              label="Includi anche il link per accettare il preventivo online"
              checked={includiLink}
              onChange={(e) => setIncludiLink(e.target.checked)}
            />
          </div>
        }
        onConfirm={handleSendPreventivo}
        onCancel={() => setShowSendModal(false)}
      />

      <ConfirmationModal
        show={showConvertModal}
        title="Segna come accettato e crea l'ordine"
        body={
          <div>
            <p>Il cliente ha accettato il preventivo {preventivo.numero_completo || `#${preventivo.id}`}? Verrà creato subito l'ordine da lavorare.</p>
            <div className="alert alert-warning mt-3">
              <strong>Attenzione:</strong> l'azione non è annullabile. Le condizioni di pagamento finiscono nelle note dell'ordine.
              <br/><strong>Stato attuale:</strong> {preventivo.stato}
            </div>
          </div>
        }
        onConfirm={handleAccettaManuale}
        onCancel={() => setShowConvertModal(false)}
      />
    </>
  );
}

export default PreventivoDetail;
